import React, { useState } from "react";
import { impersonateUser } from "@frontrunners/wl-cognito-login";
import { convertObjToBase64 } from "../../../../common/locationHelper";
import { PasswordConfirmationBox } from "./PasswordConfirmationBox";
import { LoginConfirmationBox } from "./LoginConfirmationBox";
import { ResetUserPhoneNumberModal } from "./ResetUserPhoneNumberModal";
import { ResetUserPasswordModal } from "./ResetUserPasswordModal";
import { UserTable } from "./UserTable";
import { hasValidCognitoToken } from "../../../../common/authHelper";
import { sscIamImpersonate } from "../../../../common/ssciamAPI";
import { ErrorModal } from "./ErrorModal";

// this component will be used once every supportal users have been migrated to ssciam
export const UserList = ({ endpoints, users, impersonation }) => {
  const [password, setPassword] = useState("");
  const [cognitoPasswordModalOpen, togglePasswordModal] = useState(false);
  const [cognitoLoginModalOpen, toggleCognitoLoginModal] = useState(false);
  const [error, setError] = useState(false);
  const [resetPhoneForUser, setResetPhoneForUser] = useState(null);
  const [resetPasswordForUser, setResetPasswordForUser] = useState(null);
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);
  const [userToImpersonate, setUserToImpersonate] = useState(null);
  const [impersonationError, setImpersonationError] = useState(null);

  const merchantId = impersonation?.merchantDetails?.id;
  const poolData = {
    UserPoolId: endpoints.cognitoUserPoolId,
    ClientId: endpoints.cognitoMerchantPortalClientId,
  };
  const { dxGateway, backofficeHost, portal2Host } = endpoints;

  const impersonate = (email, userID, isMigrated) => {
    setUserToImpersonate({ email, userID, isMigrated });

    // ssciam
    if (isMigrated) {
      return impersonateUserSsciam(email, merchantId, userID);
    }

    const cognitoUser = localStorage.getItem("cognitoUser");

    // cognito
    if (!hasValidCognitoToken() || !cognitoUser) {
      return toggleCognitoLoginModal(true);
    }
    // Check if we have a password, if not, ask for it
    if (!password) {
      return togglePasswordModal(true);
    }

    return impersonateUserCognito(poolData, cognitoUser, password, email, userID);
  };

  const impersonateUserSsciam = async (email, merchantId, userId) => {
    setImpersonatingUserId(userId);
    try {
      const code = await sscIamImpersonate(dxGateway, merchantId, email);
      const tabId = Math.random().toString(36).slice(-4);
      window.open(
        `${backofficeHost}/ssciam/impersonate#code=${code}&userHint=${email}&merchantHint=${merchantId}`,
        tabId
      );
    } catch (error) {
      console.error("Error impersonating user:", error);
      setImpersonationError(error.message || "An unexpected error occurred while impersonating the user.");
    } finally {
      setImpersonatingUserId(null);
    }
  };

  const impersonateUserCognito = async (poolData, username, password, targetEmail, userId) => {
    setImpersonatingUserId(userId);
    try {
      const response = await impersonateUser(poolData, username, password, targetEmail);
      const targetIdToken = response.idToken.jwtToken;

      const tabId = Math.random().toString(36).slice(-4);
      const state = convertObjToBase64({
        impersonateUserId: userId,
      });
      const query = new URLSearchParams({
        access_token: targetIdToken,
        id_token: targetIdToken,
        state,
      }).toString();

      window.open(`${endpoints.backofficeHost}/?#${query}`, tabId);
      togglePasswordModal(false);
      setError(false);
    } catch (error) {
      console.error(error);
      setError(true);
    } finally {
      setImpersonatingUserId(null);
    }
  };

  const togglePasswordConfirmation = () => {
    setError(false);
    setPassword("");
    togglePasswordModal(!cognitoPasswordModalOpen);
  };

  const impersonateSelectedUser = async () => {
    const username = localStorage.getItem("cognitoUser");
    return impersonateUserCognito(poolData, username, password, userToImpersonate.email, userToImpersonate.userID);
  };

  const onChangePassword = (value) => {
    setPassword(value);
  };

  const handleResetPassword = (user) => {
    const hasCognitoToken = hasValidCognitoToken();
    return hasCognitoToken ? setResetPasswordForUser(user) : toggleCognitoLoginModal(true);
  };

  if (!users || users.length === 0) {
    return <p>This merchant has no users</p>;
  }

  return (
    <div>
      <h2>Users</h2>
      {cognitoPasswordModalOpen && (
        <PasswordConfirmationBox
          error={error}
          togglePasswordConfirmation={togglePasswordConfirmation}
          onChange={onChangePassword}
          impersonateSelectedUser={impersonateSelectedUser}
          isImpersonating={!!impersonatingUserId}
        />
      )}
      {cognitoLoginModalOpen && (
        <LoginConfirmationBox
          toggleLoginConfirmation={toggleCognitoLoginModal}
          endpoints={endpoints}
          password={password}
          setPassword={onChangePassword}
        />
      )}

      {resetPhoneForUser && (
        <ResetUserPhoneNumberModal
          user={resetPhoneForUser}
          endpoints={endpoints}
          onClose={() => setResetPhoneForUser(null)}
        />
      )}
      {resetPasswordForUser && !cognitoLoginModalOpen && (
        <ResetUserPasswordModal
          user={resetPasswordForUser}
          endpoints={endpoints}
          onClose={() => setResetPasswordForUser(null)}
        />
      )}
      {impersonationError && (
        <ErrorModal
          title="Impersonation failed"
          message={impersonationError}
          onClose={() => setImpersonationError(null)}
        />
      )}
      <div style={{ opacity: `${cognitoPasswordModalOpen || cognitoLoginModalOpen ? 0.4 : 1}` }}>
        <UserTable
          users={users}
          onImpersonate={impersonate}
          onResetPassword={handleResetPassword}
          onResetPhoneNumber={setResetPhoneForUser}
          onRequestCognitoAuth={() => toggleCognitoLoginModal(true)}
          impersonatingUserId={impersonatingUserId}
          portal2Host={portal2Host}
        />
      </div>
    </div>
  );
};
